import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import { logisticaAPI } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Gauge,
  Activity,
  Wrench,
  CheckCircle2,
  Clock,
  MapPin,
  AlertTriangle,
  Play,
  Pause,
  RefreshCw,
  Bus,
  Truck,
  Car,
  CarFront,
  ArrowRight,
  Loader2,
} from "lucide-react";
import { cn } from "../lib/utils";

const SCOPE_STORAGE_KEY_PREFIX = "logistica_scope_";
const AUTO_REFRESH_MS = 60000;

const getUnitIcon = (vehicleType) => {
  const t = (vehicleType || "").toLowerCase();
  if (t.includes("bus") || t === "autobus" || t === "autobús") return Bus;
  if (t.includes("sprinter") || t.includes("van")) return Truck;
  if (t.includes("suv")) return CarFront;
  return Car;
};

const saturationColors = {
  ok: "bg-emerald-500",
  warning: "bg-amber-500",
  critical: "bg-rose-600",
};

const globalLevelMap = {
  low: { label: "Baja", tone: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  medium: { label: "Media", tone: "bg-amber-100 text-amber-800 border-amber-300" },
  high: { label: "Alta", tone: "bg-rose-100 text-rose-800 border-rose-300" },
};

export default function Logistica() {
  const { user } = useAuth();
  const scopeKey = `${SCOPE_STORAGE_KEY_PREFIX}${user?.id || "anon"}`;

  const [scope, setScope] = useState(() => {
    try { return localStorage.getItem(scopeKey) || "today"; } catch { return "today"; }
  });
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [allReleasesOpen, setAllReleasesOpen] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    try { localStorage.setItem(scopeKey, scope); } catch (e) { /* ignore */ }
  }, [scope, scopeKey]);

  const fetchData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const params = { scope };
      if (scope === "custom") {
        if (!customStart || !customEnd) {
          setError("Selecciona fecha/hora de inicio y fin para rango personalizado");
          setLoading(false);
          setRefreshing(false);
          return;
        }
        params.start_datetime = new Date(customStart).toISOString();
        params.end_datetime = new Date(customEnd).toISOString();
      }
      const res = await logisticaAPI.getDashboard(params);
      setData(res.data);
      setError(null);
    } catch (e) {
      const msg = e.response?.data?.detail || "Error cargando dashboard de logística";
      setError(msg);
      if (!silent) toast.error(msg);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [scope, customStart, customEnd]);

  useEffect(() => {
    fetchData(false);
  }, [fetchData]);

  // Auto-refresh
  useEffect(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (autoRefresh && scope !== "custom") {
      timerRef.current = setInterval(() => fetchData(true), AUTO_REFRESH_MS);
    } else if (autoRefresh && scope === "custom" && customStart && customEnd) {
      timerRef.current = setInterval(() => fetchData(true), AUTO_REFRESH_MS);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [autoRefresh, scope, customStart, customEnd, fetchData]);

  const globalLevel = data?.global_saturation?.level || "low";
  const globalLevelCfg = globalLevelMap[globalLevel];
  const formatTime = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleString("es-MX", {
        day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
      });
    } catch { return iso; }
  };

  const unassignedAnchor = useRef(null);

  const orderedReleasesAll = useMemo(() => data?.upcoming_releases_all || [], [data]);

  return (
    <Layout>
      <div className="p-6 lg:p-8 space-y-6" data-testid="logistica-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <h1 className="text-3xl font-heading font-bold text-slate-900 flex items-center gap-2">
              <Gauge className="w-8 h-8 text-blue-600" />
              Logística
            </h1>
            <p className="text-slate-500 mt-1">Torre de control operativa · decisiones en segundos</p>
            {data?.generated_at && (
              <p className="text-[11px] text-slate-400 mt-1 flex items-center gap-2">
                <span className={cn("inline-block w-1.5 h-1.5 rounded-full", autoRefresh ? "bg-emerald-500 animate-pulse" : "bg-slate-400")} />
                En vivo · actualizado {formatTime(data.generated_at)}
                {refreshing && <Loader2 className="w-3 h-3 animate-spin" />}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Tabs value={scope} onValueChange={setScope}>
              <TabsList data-testid="scope-tabs">
                <TabsTrigger value="today" data-testid="scope-today">Hoy</TabsTrigger>
                <TabsTrigger value="tomorrow" data-testid="scope-tomorrow">Mañana</TabsTrigger>
                <TabsTrigger value="next_7d" data-testid="scope-7d">7 días</TabsTrigger>
                <TabsTrigger value="custom" data-testid="scope-custom">Rango</TabsTrigger>
              </TabsList>
            </Tabs>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setAutoRefresh(v => !v)}
              title={autoRefresh ? "Pausar auto-refresh" : "Reanudar auto-refresh"}
              data-testid="auto-refresh-toggle"
            >
              {autoRefresh ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchData(false)}
              disabled={refreshing || loading}
              data-testid="manual-refresh-btn"
            >
              <RefreshCw className={cn("w-4 h-4", (refreshing || loading) && "animate-spin")} />
            </Button>
          </div>
        </div>

        {/* Custom range inputs */}
        {scope === "custom" && (
          <Card>
            <CardContent className="p-4 flex flex-col sm:flex-row gap-3 items-end">
              <div className="flex-1">
                <label className="text-xs text-slate-500">Inicio</label>
                <Input type="datetime-local" value={customStart} onChange={(e) => setCustomStart(e.target.value)} data-testid="custom-start" />
              </div>
              <div className="flex-1">
                <label className="text-xs text-slate-500">Fin</label>
                <Input type="datetime-local" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} data-testid="custom-end" />
              </div>
              <Button onClick={() => fetchData(false)} data-testid="apply-custom-range" disabled={!customStart || !customEnd}>
                Aplicar rango
              </Button>
            </CardContent>
          </Card>
        )}

        {/* KPIs + Global saturation */}
        {loading && !data ? (
          <div className="p-12 text-center">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400 mx-auto" />
          </div>
        ) : error && !data ? (
          <Card><CardContent className="p-6 text-center text-rose-600">{error}</CardContent></Card>
        ) : data && (
          <>
            {/* KPIs row */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="kpi-row">
              <KpiCard label="Unidades activas" value={data.kpis.total_units_active} color="slate" icon={Activity} testid="kpi-total" />
              <KpiCard label="Ocupadas ahora" value={data.kpis.units_occupied_now} color="amber" icon={Clock} testid="kpi-occupied-now"
                secondary={`${data.kpis.units_occupied_in_range} en el rango`} />
              <KpiCard label="Disponibles ahora" value={data.kpis.units_available_now} color="emerald" icon={CheckCircle2} testid="kpi-available-now" />
              <KpiCard label="En mantenimiento" value={data.kpis.units_in_maintenance} color="orange" icon={Wrench} testid="kpi-maintenance" />
              <KpiCard
                label="Sin asignar"
                value={data.kpis.unassigned_services}
                color={data.kpis.unassigned_services > 0 ? "rose" : "slate"}
                icon={AlertTriangle}
                testid="kpi-unassigned"
                onClick={() => unassignedAnchor.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
              />
            </div>

            {/* Global saturation banner */}
            <Card className={cn("border-l-4",
              globalLevel === "high" && "border-l-rose-500",
              globalLevel === "medium" && "border-l-amber-500",
              globalLevel === "low" && "border-l-emerald-500"
            )} data-testid="global-saturation">
              <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3">
                  <Gauge className={cn("w-5 h-5",
                    globalLevel === "high" && "text-rose-600",
                    globalLevel === "medium" && "text-amber-600",
                    globalLevel === "low" && "text-emerald-600"
                  )} />
                  <div>
                    <p className="text-xs text-slate-500">Saturación global del sistema</p>
                    <p className="font-semibold">
                      {data.global_saturation.pct}% <span className="text-slate-500 font-normal">· {data.global_saturation.occupied_now}/{data.global_saturation.operational} unidades operativas</span>
                    </p>
                  </div>
                </div>
                <Badge className={cn("border text-sm px-3 py-1", globalLevelCfg.tone)}>
                  {globalLevelCfg.label}
                </Badge>
              </CardContent>
            </Card>

            {/* Capacity + Releases */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Capacity by type */}
              <Card data-testid="capacity-panel">
                <CardContent className="p-4">
                  <h3 className="font-heading font-semibold mb-3">Capacidad por tipo de unidad</h3>
                  {data.capacity_by_type.length === 0 ? (
                    <p className="text-sm text-slate-500">No hay unidades activas.</p>
                  ) : (
                    <div className="space-y-3">
                      {data.capacity_by_type.map((t) => {
                        const Icon = getUnitIcon(t.type_code);
                        return (
                          <div key={t.type_code} data-testid={`capacity-${t.type_code}`}>
                            <div className="flex items-center justify-between text-sm mb-1">
                              <span className="flex items-center gap-2 font-medium text-slate-700">
                                <Icon className="w-4 h-4 text-slate-500" />
                                {t.type_name}
                              </span>
                              <span className="text-xs text-slate-600">
                                {t.available} disp. · {t.occupied}/{t.total - t.in_maintenance} ocup. · {t.utilization_pct}%
                              </span>
                            </div>
                            <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
                              <div
                                className={cn("h-2 transition-all", saturationColors[t.saturation_flag])}
                                style={{ width: `${Math.min(100, t.utilization_pct)}%` }}
                              />
                            </div>
                            {t.in_maintenance > 0 && (
                              <p className="text-[10px] text-orange-600 mt-0.5">
                                ⚠ {t.in_maintenance} en mantenimiento (excluidas del cálculo)
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Upcoming releases */}
              <Card data-testid="releases-panel">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-heading font-semibold">Próximas liberaciones</h3>
                    {orderedReleasesAll.length > 10 && (
                      <Button variant="ghost" size="sm" onClick={() => setAllReleasesOpen(true)} data-testid="view-all-releases">
                        Ver todas ({orderedReleasesAll.length})
                      </Button>
                    )}
                  </div>
                  {data.upcoming_releases.length === 0 ? (
                    <p className="text-sm text-slate-500">Ninguna unidad ocupada en este momento.</p>
                  ) : (
                    <div className="space-y-2">
                      {data.upcoming_releases.map((r) => <ReleaseRow key={r.unit_id} r={r} />)}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Unassigned services */}
            <Card ref={unassignedAnchor} data-testid="unassigned-panel">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-heading font-semibold flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-rose-600" />
                    Servicios sin asignar
                    <span className="text-sm text-slate-500 font-normal">({data.unassigned_services.length})</span>
                  </h3>
                </div>
                {data.unassigned_services.length === 0 ? (
                  <p className="text-sm text-emerald-600 flex items-center gap-1">
                    <CheckCircle2 className="w-4 h-4" /> Todo está asignado en este rango.
                  </p>
                ) : (
                  <div className="space-y-2" data-testid="unassigned-list">
                    {data.unassigned_services.map((s) => <UnassignedRow key={s.contract_id} s={s} formatTime={formatTime} />)}
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>

      {/* All releases dialog */}
      <Dialog open={allReleasesOpen} onOpenChange={setAllReleasesOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-heading">Próximas liberaciones ({orderedReleasesAll.length})</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto space-y-2 pr-1" data-testid="all-releases-list">
            {orderedReleasesAll.map((r) => <ReleaseRow key={r.unit_id} r={r} />)}
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

// ---------- Sub-components ----------

function KpiCard({ label, value, color = "slate", icon: Icon, secondary, testid, onClick }) {
  const colorMap = {
    slate: "border-l-slate-400 text-slate-700",
    amber: "border-l-amber-500 text-amber-700",
    emerald: "border-l-emerald-500 text-emerald-700",
    orange: "border-l-orange-500 text-orange-700",
    rose: "border-l-rose-500 text-rose-700",
  };
  const interactive = typeof onClick === "function";
  return (
    <Card
      className={cn("border-l-4", colorMap[color], interactive && "cursor-pointer hover:shadow-md transition-shadow")}
      onClick={onClick}
      data-testid={testid}
    >
      <CardContent className="p-3">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[11px] text-slate-500 uppercase tracking-wide">{label}</p>
            <p className={cn("text-2xl font-bold leading-tight", colorMap[color])}>{value}</p>
            {secondary && <p className="text-[10px] text-slate-400 mt-0.5">{secondary}</p>}
          </div>
          {Icon && <Icon className={cn("w-6 h-6 opacity-60", colorMap[color])} />}
        </div>
      </CardContent>
    </Card>
  );
}

function ReleaseRow({ r }) {
  const Icon = getUnitIcon(r.vehicle_type);
  const isImminent = r.release_in_minutes <= 30;
  return (
    <Link
      to={`/contratos/${r.contract_id}`}
      className="block rounded-md border p-2 hover:bg-slate-50 transition-colors"
      data-testid={`release-${r.unit_id}`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Icon className="w-4 h-4 text-slate-500 flex-shrink-0" />
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="font-mono text-sm font-medium">{r.plate || r.name}</span>
              <span className="text-xs text-slate-400">·</span>
              <span className="text-xs text-slate-600 truncate">{r.client_name}</span>
            </div>
            <p className="text-[10px] text-slate-400 truncate">Folio {r.contract_folio}</p>
          </div>
        </div>
        <Badge className={cn("text-[10px] flex-shrink-0", isImminent ? "bg-rose-100 text-rose-700" : "bg-slate-100 text-slate-700")}>
          {r.release_in_human}
        </Badge>
      </div>
    </Link>
  );
}

function UnassignedRow({ s, formatTime }) {
  return (
    <div className="rounded-lg border p-3 flex items-start justify-between gap-3 hover:bg-slate-50 transition-colors" data-testid={`unassigned-${s.contract_id}`}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-slate-900 truncate">{s.client_name || "Sin cliente"}</span>
          <Badge variant="outline" className="h-5 text-[10px] font-mono">{s.contract_folio || "—"}</Badge>
          <Badge className={cn(
            "h-5 text-[10px]",
            s.units_assigned === 0 ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-800"
          )}>
            {s.units_assigned}/{s.units_required} unidades
          </Badge>
        </div>
        <div className="flex items-center gap-3 mt-1 text-xs text-slate-600 flex-wrap">
          <span className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {formatTime(s.pickup_datetime)}
          </span>
          {s.origin && (
            <span className="flex items-center gap-1 truncate max-w-[240px]">
              <MapPin className="w-3 h-3" />
              <span className="truncate">{s.origin}{s.destination ? ` → ${s.destination}` : ""}</span>
            </span>
          )}
        </div>
        <div className="flex items-center gap-1 mt-1 flex-wrap">
          {s.missing_by_type.map((m) => {
            const Icon = getUnitIcon(m.type_code);
            return (
              <span key={m.type_code} className="inline-flex items-center gap-1 bg-slate-100 text-slate-700 rounded px-1.5 py-0.5 text-[10px]">
                <Icon className="w-3 h-3" />
                {m.count}× {m.type_name}
              </span>
            );
          })}
        </div>
      </div>
      <Link to={`/asignacion?contract_id=${s.contract_id}`} data-testid={`assign-link-${s.contract_id}`}>
        <Button size="sm" variant="outline" className="flex-shrink-0">
          Asignar <ArrowRight className="w-3 h-3 ml-1" />
        </Button>
      </Link>
    </div>
  );
}
